import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Search, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { getSales } from '../../api/sales';
import BlurredValue from '../../components/common/BlurredValue';
import ReportPreviewModal from '../../components/reports/ReportPreviewModal';
import { BranchSelector } from '../../components/branches';
import { useBranch } from '../../contexts/BranchContext';
import { formatDateToISO } from '../../utils/formatters';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SalesBookPage = () => {
  const navigate = useNavigate();
  const { isMultiBranch } = useBranch();
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [totalSales, setTotalSales] = useState(0);
  const [totalVAT, setTotalVAT] = useState(0);
  const [totalNet, setTotalNet] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [filters, setFilters] = useState({
    startDate: formatDateToISO(new Date()),
    endDate: formatDateToISO(new Date()),
    search: '',
    status: '',
    branchId: ''
  });

  useEffect(() => {
    fetchSales();
  }, [filters.startDate, filters.endDate, filters.branchId, filters.status]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const response = await getSales({
        page: 1,
        limit: 1000,
        startDate: filters.startDate,
        endDate: filters.endDate,
        search: filters.search,
        status: filters.status,
        branchId: filters.branchId
      });

      setSales(response.sales || []);

      // Calculate totals
      const total = (response.sales || []).reduce(
        (sum, sale) => sum + parseFloat(sale.total_amount),
        0
      );
      // Calculate VAT as 7.5% of net amount (VAT = Total / 1.075 * 0.075)
      const vat = total - total / 1.075;
      const net = total - vat;

      setTotalSales(total);
      setTotalVAT(vat);
      setTotalNet(net);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load sales book');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSales();
  };

  const setPresetRange = (range) => {
    const end = new Date();
    let start = new Date();

    switch (range) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setDate(start.getDate() - 30);
        break;
      case 'year':
        start.setFullYear(start.getFullYear() - 1);
        break;
      default:
        break;
    }

    setFilters((prev) => ({
      ...prev,
      startDate: formatDateToISO(start),
      endDate: formatDateToISO(end)
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Calculate VAT for individual sale (7.5%)
  const calculateVAT = (total) => {
    return parseFloat(total) - parseFloat(total) / 1.075;
  };

  const calculateNet = (total) => {
    return parseFloat(total) / 1.075;
  };

  const getDateRangeLabel = () => {
    const start = new Date(filters.startDate).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    const end = new Date(filters.endDate).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    return `${start} - ${end}`;
  };

  const handleWhatsApp = async () => {
    setSendingWhatsApp(true);
    try {
      const message = encodeURIComponent(
        `📖 *Sales Book Report*\n` +
          `📅 ${getDateRangeLabel()}\n\n` +
          `🧾 Transactions: ${sales.length}\n` +
          `💵 Net Sales: ${formatCurrency(totalNet)}\n` +
          `🏛️ VAT (7.5%): ${formatCurrency(totalVAT)}\n` +
          `💰 Total Sales: ${formatCurrency(totalSales)}`
      );
      window.open(`https://wa.me/?text=${message}`, '_blank');
      toast.success('Opening WhatsApp...');
    } catch (error) {
      toast.error('Failed to open WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Report table content for modal
  const ReportContent = () => (
    <div>
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Transactions</p>
          <p className="text-lg font-bold">{sales.length}</p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Net Sales</p>
          <p className="text-lg font-bold">{formatCurrency(totalNet)}</p>
        </div>

        <div className="text-center p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
          <p className="text-xs text-blue-600 mb-1">Total Sales</p>
          <p className="text-lg font-bold text-blue-700">{formatCurrency(totalSales)}</p>
        </div>
      </div>

      {/* Table */}
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-3 py-2 text-left">Date</th>
            <th className="border border-gray-300 px-3 py-2 text-left">Invoice No</th>
            <th className="border border-gray-300 px-3 py-2 text-left">Customer</th>
            <th className="border border-gray-300 px-3 py-2 text-left">Item</th>
            <th className="border border-gray-300 px-3 py-2 text-center">Qty</th>
            <th className="border border-gray-300 px-3 py-2 text-right">Net</th>

            <th className="border border-gray-300 px-3 py-2 text-right">Total</th>
            <th className="border border-gray-300 px-3 py-2 text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {sales.map((sale) => {
            const saleVAT = calculateVAT(sale.total_amount);
            const saleNet = calculateNet(sale.total_amount);
            return (
              <tr key={sale.id} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-3 py-2 whitespace-nowrap">
                  {formatDate(sale.created_at)}
                </td>
                <td className="border border-gray-300 px-3 py-2 font-medium text-blue-600">
                  {sale.receipt_number}
                </td>
                <td className="border border-gray-300 px-3 py-2">
                  {sale.customer_name || 'Guest Customer'}
                </td>
                <td className="border border-gray-300 px-3 py-2 max-w-xs truncate">
                  {sale.items_summary || `${sale.item_count || 0} items`}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {sale.item_count || 1}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-right">
                  {formatCurrency(saleNet)}
                </td>

                <td className="border border-gray-300 px-3 py-2 text-right font-medium">
                  {formatCurrency(sale.total_amount)}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  <span
                    className={`px-2 py-0.5 rounded text-xs ${
                      sale.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : sale.status === 'cancelled'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {sale.status === 'completed' ? 'Paid' : sale.status}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-gray-100 font-bold">
            <td colSpan="5" className="border border-gray-300 px-3 py-2 text-right">
              TOTAL
            </td>
            <td className="border border-gray-300 px-3 py-2 text-right">
              {formatCurrency(totalNet)}
            </td>

            <td className="border border-gray-300 px-3 py-2 text-right">
              {formatCurrency(totalSales)}
            </td>
            <td className="border border-gray-300 px-3 py-2"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary-offWhite p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/reports')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-primary-black">Detailed Sales Book</h1>
                <p className="text-secondary-gray text-sm mt-1">
                  Comprehensive ledger of all sales transactions
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {isMultiBranch && (
                <BranchSelector
                  selectedBranchId={filters.branchId}
                  onBranchChange={(branchId) => setFilters((prev) => ({ ...prev, branchId }))}
                  showAllOption={true}
                  className="w-full sm:w-auto"
                />
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowReportModal(true)}
                  disabled={loading || sales.length === 0}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none"
                >
                  <FileText className="w-4 h-4" />
                  <span className="whitespace-nowrap">Generate Report</span>
                </button>
                <button
                  onClick={fetchSales}
                  className="p-2 border rounded-lg hover:bg-gray-50 text-gray-600"
                  title="Refresh"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-6 flex flex-col lg:flex-row gap-4">
            <div className="flex flex-wrap gap-2">
              {['today', 'yesterday', 'week', 'month', 'year'].map((range) => (
                <button
                  key={range}
                  onClick={() => setPresetRange(range)}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md capitalize"
                >
                  {range}
                </button>
              ))}
            </div>

            <div className="flex-1 flex flex-col sm:flex-row gap-4 items-end sm:items-center">
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="px-3 py-2 border rounded-lg"
                />
              </div>

              <form onSubmit={handleSearch} className="flex-1 w-full relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search receipt #, customer..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg"
                />
              </form>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-accent-gold">
            <p className="text-gray-500 text-sm">Total Sales Value</p>
            <p className="text-2xl font-bold text-primary-black">
              <BlurredValue value={formatCurrency(totalSales)} />
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-blue-500">
            <p className="text-gray-500 text-sm">Net Sales</p>
            <p className="text-2xl font-bold text-primary-black">
              <BlurredValue value={formatCurrency(totalNet)} />
            </p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-green-500">
            <p className="text-gray-500 text-sm">Transaction Count</p>
            <p className="text-2xl font-bold text-primary-black">{sales.length}</p>
          </div>
        </div>

        {/* Sales Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <LoadingSpinner message="Loading sales transactions..." className="py-12" />
          ) : sales.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No sales found for the selected period.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-600 font-medium border-b">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Invoice No</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3 text-center">Qty</th>
                    <th className="px-4 py-3 text-right">Net</th>

                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sales.map((sale) => {
                    const saleVAT = calculateVAT(sale.total_amount);
                    const saleNet = calculateNet(sale.total_amount);
                    return (
                      <tr key={sale.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatDate(sale.created_at)}
                          <div className="text-xs text-gray-400">
                            {new Date(sale.created_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium text-blue-600">
                          {sale.receipt_number}
                        </td>
                        <td className="px-4 py-3">
                          {sale.customer_name || 'Guest Customer'}
                          {sale.referrer_name && (
                            <div className="text-xs text-gray-400">Ref: {sale.referrer_name}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-xs truncate" title={sale.items_summary}>
                          {sale.items_summary || `${sale.item_count || 0} items`}
                        </td>
                        <td className="px-4 py-3 text-center">{sale.item_count || 1}</td>
                        <td className="px-4 py-3 text-right">
                          <BlurredValue value={formatCurrency(saleNet)} />
                        </td>

                        <td className="px-4 py-3 text-right font-medium">
                          <BlurredValue value={formatCurrency(sale.total_amount)} />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              sale.status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : sale.status === 'cancelled'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {sale.status === 'completed' ? 'Paid' : sale.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 font-bold">
                  <tr>
                    <td colSpan="5" className="px-4 py-3 text-right">
                      TOTAL
                    </td>
                    <td className="px-4 py-3 text-right">
                      <BlurredValue value={formatCurrency(totalNet)} />
                    </td>

                    <td className="px-4 py-3 text-right">
                      <BlurredValue value={formatCurrency(totalSales)} />
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Report Preview Modal */}
      <ReportPreviewModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Sales Book Report"
        subtitle={getDateRangeLabel()}
        onDownload={() => {
          toast.info('PDF download coming soon');
        }}
        onWhatsApp={handleWhatsApp}
        sendingWhatsApp={sendingWhatsApp}
      >
        <ReportContent />
      </ReportPreviewModal>
    </div>
  );
};

export default SalesBookPage;
